package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.Urlaubsanspruch
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date
import kotlin.js.Promise
import kotlin.math.round

@Composable
internal fun ZeitprofileView(
    users: List<All>,
    config: ZeitprofilConfig,
    selectedProfileId: String,
    saving: Boolean,
    message: String,
    onSelectProfile: (String) -> Unit,
    onConfigChange: (ZeitprofilConfig) -> Unit,
    onAddProfile: () -> Unit,
    onSave: () -> Unit
) {
    Style(KontingentStyles)
    val selected = config.profile.firstOrNull { it.id == selectedProfileId }

    fun updateProfile(next: PersonalZeitprofil) {
        onConfigChange(config.copy(profile = config.profile.map { if (it.id == next.id) next else it }))
    }

    Div({ classes(KontingentStyles.simpleProfilePage) }) {
        Div({ classes(KontingentStyles.profileToolbar) }) {
            Div({ classes(KontingentStyles.profileTabs) }) {
                config.profile.forEach { profile ->
                    LumaticaButton(
                        label = profile.name.ifBlank { "Zeitprofil" },
                        onClick = { onSelectProfile(profile.id) },
                        variant = LumaticaButtonVariant.State,
                        isActive = profile.id == selectedProfileId,
                        heightPx = 32
                    )
                }
                LumaticaButton(label = "+ Profil", onClick = onAddProfile, heightPx = 32)
            }
            LumaticaButton(
                label = if (saving) "Speichern..." else "Speichern",
                onClick = onSave,
                variant = LumaticaButtonVariant.Action,
                enabled = !saving && config.profile.isNotEmpty(),
                heightPx = 34
            )
        }
        if (message.isNotBlank()) {
            Span({ classes(KontingentStyles.message) }) { Text(message) }
        }
        if (selected != null) {
            Div({ classes(KontingentStyles.simpleProfileCard) }) {
                KontingentInput("Profilname", selected.name) { updateProfile(selected.copy(name = it)) }
                Div({ classes(KontingentStyles.inlineInputs) }) {
                    ZeitprofilTimeInput("Arbeitsbeginn", selected.start) { updateProfile(selected.copy(start = it)) }
                    ZeitprofilTimeInput("Arbeitsende", selected.ende) { updateProfile(selected.copy(ende = it)) }
                }
                Div({ classes(KontingentStyles.daySelector) }) {
                    zeitArbeitstagKeys.forEach { (key, label) ->
                        val active = selected.arbeitstage.ifEmpty { defaultZeitArbeitstage() }[key] == true
                        Button(attrs = {
                            classes(KontingentStyles.dayButton)
                            if (active) classes(KontingentStyles.dayButtonActive)
                            onClick {
                                updateProfile(
                                    selected.copy(
                                        arbeitstage = (selected.arbeitstage.ifEmpty { defaultZeitArbeitstage() }) + (key to !active)
                                    )
                                )
                            }
                        }) { Text(label) }
                    }
                }
                Div({ classes(KontingentStyles.pauseHeader) }) {
                    Span({ classes(KontingentStyles.cardTitle) }) { Text("Automatische Pausen") }
                    LumaticaButton(
                        label = "+ Pause hinzufügen",
                        onClick = {
                            updateProfile(
                                selected.copy(
                                    pausen = selected.pausen + ZeitprofilPause(
                                        id = "pause-${Date().getTime().toLong()}"
                                    )
                                )
                            )
                        },
                        heightPx = 32
                    )
                }
                if (selected.pausen.isEmpty()) {
                    Span({ classes(KontingentStyles.emptyText) }) { Text("Keine automatische Pause.") }
                }
                selected.pausen.forEach { pause ->
                    Div({ classes(KontingentStyles.pauseRow) }) {
                        KontingentInput("Bezeichnung", pause.label) { value ->
                            updateProfile(selected.copy(pausen = selected.pausen.map {
                                if (it.id == pause.id) it.copy(label = value) else it
                            }))
                        }
                        ZeitprofilTimeInput("Von", pause.von) { value ->
                            updateProfile(selected.copy(pausen = selected.pausen.map {
                                if (it.id == pause.id) it.copy(von = value) else it
                            }))
                        }
                        ZeitprofilTimeInput("Bis", pause.bis) { value ->
                            updateProfile(selected.copy(pausen = selected.pausen.map {
                                if (it.id == pause.id) it.copy(bis = value) else it
                            }))
                        }
                        LumaticaButton(
                            label = "Entfernen",
                            onClick = {
                                updateProfile(selected.copy(
                                    pausen = selected.pausen.filterNot { it.id == pause.id }
                                ))
                            },
                            heightPx = 32
                        )
                    }
                }
                Span({ classes(KontingentStyles.cardTitle) }) { Text("Mitarbeiter") }
                Div({ classes(KontingentStyles.simpleUserGrid) }) {
                    users.forEach { user ->
                        val checked = config.zuordnungen[user.id] == selected.id
                        Div({ classes(KontingentStyles.assignmentChip) }) {
                            LumaticaCheckbox(
                                checked = checked,
                                onChange = {
                                    val assignments = if (checked) {
                                        config.zuordnungen - user.id
                                    } else {
                                        config.zuordnungen + (user.id to selected.id)
                                    }
                                    onConfigChange(config.copy(zuordnungen = assignments))
                                },
                                fontSizePx = 17
                            )
                            Span({ classes(KontingentStyles.assignmentLabel) }) {
                                Text(kontingentUserName(user))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ZeitprofilTimeInput(label: String, value: String, onChange: (String) -> Unit) {
    Div({ classes(KontingentStyles.inputGroup) }) {
        Span({ classes(KontingentStyles.inputLabel) }) { Text(label) }
        Input(InputType.Time, attrs = {
            classes(KontingentStyles.input)
            value(value)
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun KontingentInput(label: String, value: String, onChange: (String) -> Unit) {
    Div({ classes(KontingentStyles.inputGroup) }) {
        Span({ classes(KontingentStyles.inputLabel) }) { Text(label) }
        Input(InputType.Text, attrs = {
            classes(KontingentStyles.input)
            value(value)
            onInput { onChange(it.value) }
        })
    }
}

internal fun defaultZeitprofil(): PersonalZeitprofil =
    PersonalZeitprofil(
        id = "standard",
        name = "Standard",
        start = "08:00",
        ende = "17:00",
        arbeitstage = defaultZeitArbeitstage(),
        pausen = emptyList()
    )

private fun kontingentUserName(user: All): String =
    user.name.ifBlank { listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ") }
        .ifBlank { user.email.ifBlank { user.id } }

private object KontingentStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        padding(18.px, 22.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
        color(Color.white)
        overflow("hidden")
    }
    val shell by style {
        width(100.percent)
        maxWidth(1320.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val header by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        flexWrap(FlexWrap.Nowrap)
        gap(12.px)
        boxSizing("border-box")
    }
    val topbarTitle by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        gap(10.px)
        minWidth(0.px)
        flexShrink(0)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val subtitle by style {
        display(DisplayStyle.Block)
        marginTop(3.px)
        color(rgba(255, 255, 255, 0.54))
        fontSize(12.px)
    }
    val headerActions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(8.px)
        flexWrap(FlexWrap.Nowrap)
        property("flex", "1 1 auto")
        minWidth(0.px)
    }
    val content by style {
        minHeight(0.px)
        overflowY("auto")
        paddingRight(4.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(22.px)
    }
    val tabs by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(7.px)
        flexWrap(FlexWrap.Wrap)
    }
    val sectionToolbar by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
    }
    val modeSwitch by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        flexWrap(FlexWrap.Nowrap)
    }
    val userDropdown by style {
        width(190.px)
        maxWidth(100.percent)
        property("flex", "0 1 190px")
    }
    val singleView by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val teamList by style {
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        boxSizing("border-box")
    }
    val employeeCard by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val employeeCardHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
    }
    val employeeHeaderPanel by style {
        padding(22.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(14.px)
        flexWrap(FlexWrap.Wrap)
    }
    val employeeHeader by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(14.px)
    }
    val employeeText by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
    }
    val employeeTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(22.px)
        fontWeight("900")
        lineHeight("1.1")
    }
    val employeeSubtitle by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(12.px)
        fontWeight("800")
    }
    val employeeActions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
        flexWrap(FlexWrap.Wrap)
    }
    val employeeDropdown by style {
        width(240.px)
    }
    val qrButtonIcon by style {
        width(16.px)
        height(16.px)
        property("filter", "brightness(0) invert(1)")
    }
    val dialogOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "1200")
        backgroundColor(rgba(0, 0, 0, 0.62))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(20.px)
        boxSizing("border-box")
    }
    val dialogBox by style {
        width(380.px)
        maxWidth(100.percent)
        padding(18.px)
        borderRadius(8.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        backgroundColor(rgb(29, 31, 35))
        property("box-shadow", "0 22px 70px rgba(0, 0, 0, 0.48)")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        boxSizing("border-box")
    }
    val dialogTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(18.px)
        fontWeight("900")
    }
    val dialogActions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
    }
    val qrDialogContent by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(12.px)
    }
    val qrDialogImage by style {
        width(300.px)
        maxWidth(100.percent)
        property("aspect-ratio", "1")
        borderRadius(8.px)
        backgroundColor(Color.white)
        padding(12.px)
        boxSizing("border-box")
    }
    val qrDialogPlaceholder by style {
        width(300.px)
        maxWidth(100.percent)
        property("aspect-ratio", "1")
        borderRadius(8.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(rgba(255, 255, 255, 0.54))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(30.px)
        fontWeight("900")
        boxSizing("border-box")
    }
    val qrDialogLabel by style {
        width(100.percent)
        color(Color.white)
        fontSize(16.px)
        fontWeight("900")
        textAlign("center")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
    val boxGrid by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Stretch)
        flexWrap(FlexWrap.Nowrap)
        gap(8.px)
        overflowX("auto")
        overflowY("hidden")
        paddingBottom(2.px)
    }
    val bigMetricBox by style {
        minHeight(58.px)
        minWidth(112.px)
        property("flex", "1 0 112px")
        padding(8.px, 10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(0, 0, 0, 0.18))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.SpaceBetween)
        gap(8.px)
        boxSizing("border-box")
    }
    val bigMetricLabel by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(10.px)
        fontWeight("900")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
    val bigMetricValue by style {
        color(Color.white)
        fontSize(16.px)
        fontWeight("900")
        lineHeight("1")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
    val metricGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
        gap(8.px)
    }
    val metricTile by style {
        minWidth(0.px)
        padding(10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.10))
        borderRadius(5.px)
        backgroundColor(rgba(0, 0, 0, 0.14))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        boxSizing("border-box")
    }
    val metricLabel by style {
        color(rgba(255, 255, 255, 0.52))
        fontSize(10.px)
        fontWeight("800")
    }
    val metricValue by style {
        color(Color.white)
        fontSize(14.px)
        fontWeight("900")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
    val requestList by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(420px, 1fr))")
        gap(14.px)
        alignContent(AlignContent.Start)
        padding(2.px)
        boxSizing("border-box")
    }
    val requestCard by style {
        padding(14.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
    }
    val requestCardHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val userCell by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(9.px)
    }
    val userText by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
    }
    val userName by style {
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
        fontSize(12.px)
        fontWeight("800")
    }
    val personnelNumber by style {
        color(rgba(255, 255, 255, 0.44))
        fontSize(10.px)
    }
    val metric by style {
        fontSize(13.px)
        fontWeight("900")
        textAlign("center")
    }
    val stateText by style {
        fontSize(12.px)
        fontWeight("800")
    }
    val actions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        gap(6.px)
        flexWrap(FlexWrap.Wrap)
    }
    val profileLayout by style {
        minHeight(0.px)
        flexGrow(1)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "300px minmax(0, 1fr)")
        gap(14.px)
        overflow("hidden")
    }
    val simpleProfilePage by style {
        width(100.percent)
        maxWidth(820.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        overflow("auto")
        padding(2.px)
        boxSizing("border-box")
    }
    val profileToolbar by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val profileTabs by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        flexWrap(FlexWrap.Wrap)
    }
    val simpleProfileCard by style {
        padding(16.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        boxSizing("border-box")
    }
    val pauseHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val pauseRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(150px, 1fr) 120px 120px auto")
        alignItems(AlignItems.End)
        gap(8.px)
    }
    val simpleUserGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
        gap(7.px)
    }
    val profileEditor by style {
        minHeight(0.px)
        padding(14.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val cardTitle by style {
        fontSize(14.px)
        fontWeight("900")
    }
    val smallTitle by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(11.px)
        fontWeight("800")
    }
    val inlineInputs by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        gap(8.px)
    }
    val daySelector by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        flexWrap(FlexWrap.Wrap)
    }
    val dayButton by style {
        width(38.px)
        height(30.px)
        padding(0.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(5.px)
        backgroundColor(rgba(0, 0, 0, 0.20))
        color(rgba(255, 255, 255, 0.62))
        fontSize(11.px)
        fontWeight("900")
        cursor("pointer")
    }
    val dayButtonActive by style {
        backgroundColor(Color("#66BB6A"))
        property("border-color", "#66BB6A")
        color(Color("#111418"))
    }
    val inputGroup by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
    }
    val inputLabel by style {
        color(rgba(255, 255, 255, 0.56))
        fontSize(10.px)
        fontWeight("800")
    }
    val input by style {
        width(100.percent)
        height(32.px)
        padding(0.px, 9.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(5.px)
        backgroundColor(rgba(0, 0, 0, 0.22))
        color(Color.white)
        boxSizing("border-box")
    }
    val assignmentList by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val assignmentRow by style {
        padding(12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(220px, 280px) repeat(3, minmax(0, 1fr))")
        gap(12.px)
        alignItems(AlignItems.Start)
        boxSizing("border-box")
    }
    val assignmentGroup by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(7.px)
    }
    val assignmentTitle by style {
        color(rgba(255, 255, 255, 0.54))
        fontSize(10.px)
        fontWeight("900")
        property("text-transform", "uppercase")
    }
    val assignmentChips by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        gap(6.px)
    }
    val assignmentChip by style {
        minHeight(28.px)
        padding(4.px, 7.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.10))
        borderRadius(5.px)
        backgroundColor(rgba(0, 0, 0, 0.12))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        boxSizing("border-box")
    }
    val assignmentLabel by style {
        fontSize(11.px)
        fontWeight("800")
        color(rgba(255, 255, 255, 0.82))
        property("white-space", "nowrap")
    }
    val checkboxCell by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
    }
    val processing by style {
        width(16.px)
        color(rgba(255, 255, 255, 0.62))
        fontSize(15.px)
        textAlign("center")
    }
    val emptyState by style {
        padding(18.px)
        color(rgba(255, 255, 255, 0.62))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.10))
        borderRadius(6.px)
    }
    val emptyText by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(11.px)
        fontWeight("700")
    }
    val message by style {
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("700")
    }
}
